/**
 * The source flow (Design Review V-11; TRD 10.2 `GET /forecast/source-flow`; Phase 45), shared
 * by the read model behind the endpoint, the browser card and the CSV export, so the three
 * cannot disagree about where a role's hours came from, how wide a flow is drawn, or what the
 * table behind the chart says.
 *
 * The question
 * ------------
 * **How much of each role's demand is known, and how much did the run make up?** The quality
 * metrics answer it for the company in one percentage; the ribbon answers it for the company
 * week by week. Neither says that development is 80 per cent explicit and strategy 70 per cent
 * inferred — which is the only form of the answer a planner can act on, because it says whose
 * number to trust and whose to go and fix.
 *
 * The shape
 * ---------
 * Left nodes are the run's **source modes**, right nodes are **roles** (a canonical service,
 * keyed exactly as the heatmap's service row and the bench's roles are), and a flow is the hours
 * one mode put on one role over the window. Every allocation row in the window is in exactly one
 * flow, so the left column, the right column and the middle all add to the same total.
 *
 * Hue is the **layer**, not the mode
 * ----------------------------------
 * The modes partition into the ribbon's five layers (`layers.js`) and the flow wears its layer's
 * hue slot and its layer's fill — hatched for inferred, dotted for pipeline (PRD 7.3). So two
 * modes of one layer (`inferred_schedule` and `inferred_role_mix`) are the same colour, sit
 * adjacent in the column, and are told apart by their labels and by position, exactly as they
 * are in the ribbon's own stack. Nothing here invents a second categorical vocabulary, which
 * means there is no new adjacency to put through the colour-vision gate, and folding the column
 * from modes to layers changes the number of nodes and no colour at all.
 *
 * Percent explicit
 * ----------------
 * A role's `explicit_share` is its `explicit` flow over its total — the same numerator and the
 * same denominator as the quality card's `pct_hours_inferred` (TRD 11), one row at a time. It is
 * a share of **everything** the run forecast for the role, non-project baseline and weighted
 * pipeline included, because that is what "how much of this number is real" has to mean if the
 * number is the one on the heatmap. `explicit_of_committed` is the other reading — explicit over
 * explicit plus inferred — and the table carries it beside the first rather than instead of it.
 *
 * Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { LAYERS, SOURCE_MODES, layerOf, pipelineLayerLabel } from './layers.js';
import { bandPath } from './contention.js';

/** The two granularities of the left column: the modes themselves, or the ribbon's layers. */
export const SOURCE_FLOW_GROUPINGS = Object.freeze(['mode', 'layer']);

/** How the roles down the right may be ordered. */
export const SOURCE_FLOW_ORDERS = Object.freeze(['hours', 'explicit', 'name']);

/**
 * Source modes whose hours are a named assignment in Workamajig — what "explicit" means on this
 * card and in TRD 11's own split, which the quality metrics use for the same word.
 */
export const EXPLICIT_MODES = Object.freeze(['explicit']);

/** Source modes the run inferred rather than read (TRD 11). */
export const INFERRED_MODES = Object.freeze(['inferred_schedule', 'inferred_role_mix']);

/** What each mode is called on screen. The API's `source_mode` values are not English. */
export const SOURCE_MODE_LABELS = Object.freeze({
  explicit: 'Explicit assignments',
  inferred_schedule: 'Inferred schedule',
  inferred_role_mix: 'Inferred role mix',
  recurring_baseline: 'Recurring baseline',
  recurring_seasonal: 'Recurring seasonal',
  nonproject_baseline: 'Non-project baseline',
  pipeline: 'Pipeline',
});

/** One line per mode saying where its hours come from, for the tooltip and the table. */
export const SOURCE_MODE_NOTES = Object.freeze({
  explicit: 'a person, a service and dates on the job in Workamajig',
  inferred_schedule: 'a real assignment with no dates, spread over a window the run invented',
  inferred_role_mix: 'no assignments at all — the remaining hours split by an analog role mix',
  recurring_baseline: 'a retainer, from what it has actually consumed',
  recurring_seasonal: 'a retainer, blended with the same period last year',
  nonproject_baseline: 'meetings, admin and the rest of the week nobody bills',
  pipeline: 'an open deal, at the probability its stage was weighted by',
});

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * The modes in the order the ribbon stacks their layers, bottom of the stack first, with the
 * layer each belongs to and the hue and fill it is drawn in. Every mode the database allows is
 * here exactly once, which is what makes the left column a partition of the allocation rows.
 * @param {'weighted' | 'unweighted' | 'none'} pipelineMode
 */
export function sourceModeVocabulary(pipelineMode = 'weighted') {
  const out = [];
  for (const layer of LAYERS)
    for (const mode of layer.source_modes)
      out.push({
        key: mode,
        label: mode === 'pipeline' ? pipelineLayerLabel(pipelineMode) : SOURCE_MODE_LABELS[mode],
        note: SOURCE_MODE_NOTES[mode] ?? '',
        layer: layer.key,
        layer_label: layer.key === 'pipeline' ? pipelineLayerLabel(pipelineMode) : layer.label,
        token: layer.token,
        fill: layer.fill,
        confidence_tier: layer.confidence_tier,
      });
  return out;
}

/** The layers as left nodes: the same vocabulary the ribbon publishes, in stack order. */
export function layerNodes(pipelineMode = 'weighted') {
  return LAYERS.map((layer) => ({
    key: layer.key,
    label: layer.key === 'pipeline' ? pipelineLayerLabel(pipelineMode) : layer.label,
    note: layer.source_modes.map((m) => SOURCE_MODE_LABELS[m]).join(' and '),
    layer: layer.key,
    layer_label: layer.key === 'pipeline' ? pipelineLayerLabel(pipelineMode) : layer.label,
    token: layer.token,
    fill: layer.fill,
    confidence_tier: layer.confidence_tier,
    source_modes: [...layer.source_modes],
  }));
}

/** Every mode the database allows belongs to a layer; the tests hold this to the CHECK list. */
export const MODE_ORDER = Object.freeze(sourceModeVocabulary().map((m) => m.key));

/** The key of one flow, so the server, the card and the export name a band the same way. */
export function flowKeyOf(source, role) {
  return `${source}|${role}`;
}

/**
 * A share, or null when there is nothing to take a share of. Null is not zero: a role with no
 * hours has no percentage explicit, and drawing one at 0 per cent would be a claim.
 * @param {number} part
 * @param {number} whole
 */
export function share(part, whole) {
  const total = Number(whole) || 0;
  if (total <= 0) return null;
  return round2(Number(part) / total);
}

// ---------------------------------------------------------------------------------------------
// Model

const matches = (role, needle) =>
  [role.label, role.key].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));

const orderComparator = (order) => {
  const byName = (a, b) =>
    String(a.label).localeCompare(String(b.label)) || a.key.localeCompare(b.key);
  if (order === 'name') return byName;
  // Least known first: the roles whose numbers are worth the least trust are the ones the reader
  // came for. A role with no share at all (no hours) sorts last, not first.
  if (order === 'explicit')
    return (a, b) =>
      Number(a.explicit_share === null) - Number(b.explicit_share === null) ||
      (a.explicit_share ?? 0) - (b.explicit_share ?? 0) ||
      b.hours - a.hours ||
      byName(a, b);
  return (a, b) => b.hours - a.hours || byName(a, b);
};

/**
 * Everything the card draws, from a `GET /forecast/source-flow` response.
 *
 * The controls hide roles in the browser, never on the server, so a role's own percent explicit
 * is about the run and not about what is ticked. What a hidden role takes with it is counted and
 * stated: a left node is as tall as the flows still drawn, and it carries the response's own
 * total alongside so the card can say the two differ.
 *
 * @param {object} data  a `GET /forecast/source-flow` response
 * @param {{ grouping?: 'mode'|'layer', order?: string, minHours?: number, q?: string }} [opts]
 */
export function sourceFlowModel(
  data,
  { grouping = 'mode', order = 'hours', minHours = 0, q = '' } = {},
) {
  if (!data?.from) return null;
  const group = SOURCE_FLOW_GROUPINGS.includes(grouping) ? grouping : 'mode';
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();
  const floor = Number(minHours) > 0 ? Number(minHours) : 0;

  const allRoles = data.roles ?? [];
  const shownRoles = allRoles.filter((r) => r.hours >= floor && (!needle || matches(r, needle)));
  const shownKeys = new Set(shownRoles.map((r) => r.key));

  // The left column's vocabulary, at the requested granularity, from the response's own list so
  // the pipeline's label still says which mode it was read in.
  const vocabulary = group === 'layer' ? (data.layers ?? []) : (data.modes ?? []);
  const sourceBy = new Map(vocabulary.map((s) => [s.key, s]));

  // One flow per source node and role, folded when the column is folded. A response flow already
  // carries its layer, so folding is a re-key and never a second definition of what a layer is.
  const folded = new Map();
  for (const f of data.flows ?? []) {
    if (!shownKeys.has(f.role)) continue;
    const source = group === 'layer' ? f.layer : f.source_mode;
    const key = flowKeyOf(source, f.role);
    const existing = folded.get(key);
    if (existing) {
      existing.hours = round2(existing.hours + f.hours);
      existing.source_modes.push(f.source_mode);
    } else {
      const node = sourceBy.get(source);
      folded.set(key, {
        key,
        source,
        source_label: node?.label ?? source,
        layer: f.layer,
        role: f.role,
        role_label: f.role_label,
        hours: f.hours,
        token: node?.token ?? '--type-other',
        fill: node?.fill ?? 'solid',
        confidence_tier: node?.confidence_tier ?? null,
        source_modes: [f.source_mode],
      });
    }
  }
  const flows = [...folded.values()].filter((f) => f.hours > 0);

  // A node is as tall as the flows drawn on it — the contention map's rule for an arc, for the
  // same reason: a block wider than its bands would be a width the data does not support.
  const drawn = new Map();
  for (const f of flows) {
    for (const key of [`s:${f.source}`, `r:${f.role}`]) {
      const entry = drawn.get(key) ?? { hours: 0, flows: 0 };
      entry.hours = round2(entry.hours + f.hours);
      entry.flows += 1;
      drawn.set(key, entry);
    }
  }

  const sources = vocabulary
    .map((s) => {
      const d = drawn.get(`s:${s.key}`) ?? { hours: 0, flows: 0 };
      const total = num(s.hours) ?? 0;
      return {
        ...s,
        hours: d.hours,
        response_hours: total,
        flows: d.flows,
        share: share(d.hours, data.totals?.hours ?? 0),
      };
    })
    .filter((s) => s.hours > 0 || s.response_hours > 0);

  const roles = shownRoles
    .map((r) => {
      const d = drawn.get(`r:${r.key}`) ?? { hours: 0, flows: 0 };
      return { ...r, drawn_hours: d.hours, drawn_flows: d.flows };
    })
    .sort(orderComparator(order));

  const hiddenRoles = allRoles.filter((r) => !shownKeys.has(r.key));
  return {
    from: data.from,
    to: data.to,
    weeks_count: data.weeks_count ?? 0,
    dimension: data.dimension ?? 'company',
    entity: data.entity ?? null,
    pipeline_mode: data.pipeline_mode ?? 'weighted',
    nonproject_mode: data.nonproject_mode ?? 'demand',
    grouping: group,
    order,
    min_hours: floor,
    sources,
    roles,
    flows,
    totals: data.totals ?? null,
    hidden_roles: hiddenRoles.length,
    hidden_hours: round2(hiddenRoles.reduce((s, r) => s + r.hours, 0)),
  };
}

/** The figures the card's header and notes state, counted over what is drawn. */
export function sourceFlowSummary(model) {
  const roles = model?.roles ?? [];
  const hours = round2(roles.reduce((s, r) => s + r.hours, 0));
  const explicit = round2(roles.reduce((s, r) => s + r.explicit_hours, 0));
  const inferred = round2(roles.reduce((s, r) => s + r.inferred_hours, 0));
  // The roles by how little of them is known, least first, ties to the one with more hours —
  // "nothing explicit" matters more when there are more hours of it. There is no size floor on
  // purpose: a four-hour role that is entirely inferred *is* entirely inferred, and the card
  // prints the hours beside the percentage so a reader can see which kind of figure it is.
  const ranked = roles
    .filter((r) => r.explicit_share !== null && r.hours > 0)
    .sort((a, b) => a.explicit_share - b.explicit_share || b.hours - a.hours);
  return {
    roles: roles.length,
    flows: model?.flows?.length ?? 0,
    sources: (model?.sources ?? []).filter((s) => s.hours > 0).length,
    hours,
    explicit_hours: explicit,
    inferred_hours: inferred,
    explicit_share: share(explicit, hours),
    inferred_share: share(inferred, hours),
    least_known: ranked[0] ?? null,
    best_known: ranked.at(-1) ?? null,
    largest: roles.reduce((best, r) => (best === null || r.hours > best.hours ? r : best), null),
    unmapped_hours: roles.find((r) => r.key === 'unmapped')?.hours ?? 0,
    hidden_roles: model?.hidden_roles ?? 0,
    hidden_hours: model?.hidden_hours ?? 0,
  };
}

/** Everything touching one node or flow key: what stays lit when the reader hovers it. */
export function incident(model, key) {
  if (!key) return null;
  const flows = new Set();
  const nodes = new Set();
  for (const f of model?.flows ?? []) {
    if (f.key === key || `s:${f.source}` === key || `r:${f.role}` === key) {
      flows.add(f.key);
      nodes.add(`s:${f.source}`);
      nodes.add(`r:${f.role}`);
    }
  }
  if (String(key).startsWith('s:') || String(key).startsWith('r:')) nodes.add(key);
  return { key, flows, nodes };
}

// ---------------------------------------------------------------------------------------------
// The diagram

/**
 * The Sankey: source modes down the left, roles down the right, a band per flow as thick as its
 * hours at both ends.
 *
 * **One scale for both columns.** The two columns hold the same hours — every allocation row is
 * in exactly one flow — so a single hours-per-pixel unit makes a band the same thickness at each
 * end and the two columns the same height. That is the property the whole chart rests on: if the
 * left column were scaled to fill the frame independently of the right, a band would taper and
 * its width would no longer be the hours.
 *
 * Bands leave a node in the order of the node at the other end, which is the fewest crossings
 * the fixed orders allow — the bipartite alternate's own rule, and `bandPath` is literally its
 * path, because a flow between two columns is the same object drawn for a different question.
 *
 * @param {{ sources: object[], roles: object[], flows: object[] }} model
 * @param {{ width?: number, height?: number, gap?: number, blockWidth?: number, top?: number }} [opts]
 */
export function sankeyLayout(
  model,
  { width = 640, height = 420, gap = 6, blockWidth = 12, top = 0 } = {},
) {
  const flows = (model?.flows ?? []).filter((f) => f.hours > 0);
  const sources = (model?.sources ?? []).filter((s) => s.hours > 0);
  const roles = (model?.roles ?? []).filter((r) => r.drawn_hours > 0);
  if (!flows.length || !sources.length || !roles.length)
    return { sources: [], roles: [], bands: [], unit: 0, height, width, blockWidth, total: 0 };

  const total = round2(flows.reduce((s, f) => s + f.hours, 0));
  const leftRoom = Math.max(1, height - gap * Math.max(0, sources.length - 1));
  const rightRoom = Math.max(1, height - gap * Math.max(0, roles.length - 1));
  // The tighter of the two, so neither column overflows the frame it was given.
  const unit = total > 0 ? Math.min(leftRoom, rightRoom) / total : 0;

  const place = (rows, sizeOf, x) => {
    let y = top;
    return rows.map((row) => {
      const h = Math.max(1, sizeOf(row) * unit);
      const box = { key: row.key, row, x, y, top: y, height: h, bottom: y + h, cursor: y };
      y += h + gap;
      return box;
    });
  };

  const left = place(sources, (s) => s.hours, 0);
  const right = place(roles, (r) => r.drawn_hours, width - blockWidth);
  const leftBy = new Map(left.map((b) => [b.key, b]));
  const rightBy = new Map(right.map((b) => [b.key, b]));
  const leftIndex = new Map(left.map((b, i) => [b.key, i]));
  const rightIndex = new Map(right.map((b, i) => [b.key, i]));

  const bands = [];
  for (const flow of [...flows].sort(
    (a, b) =>
      (leftIndex.get(a.source) ?? 0) - (leftIndex.get(b.source) ?? 0) ||
      (rightIndex.get(a.role) ?? 0) - (rightIndex.get(b.role) ?? 0),
  )) {
    const l = leftBy.get(flow.source);
    if (!l) continue;
    const span = Math.max(0.5, flow.hours * unit);
    const a = { start: l.cursor, end: l.cursor + span };
    l.cursor += span;
    bands.push({ key: flow.key, flow, a, span });
  }
  for (const band of bands.sort(
    (x, y) =>
      (rightIndex.get(x.flow.role) ?? 0) - (rightIndex.get(y.flow.role) ?? 0) ||
      (leftIndex.get(x.flow.source) ?? 0) - (leftIndex.get(y.flow.source) ?? 0),
  )) {
    const rb = rightBy.get(band.flow.role);
    if (!rb) continue;
    band.b = { start: rb.cursor, end: rb.cursor + band.span };
    rb.cursor += band.span;
    band.path = bandPath({ x0: blockWidth, x1: width - blockWidth, a: band.a, b: band.b });
  }

  return {
    sources: left,
    roles: right,
    bands: bands.filter((b) => b.b),
    unit,
    total,
    width,
    blockWidth,
    height: Math.max(left.at(-1)?.bottom ?? 0, right.at(-1)?.bottom ?? 0),
  };
}

// ---------------------------------------------------------------------------------------------
// Exports

/** One row per role: the gate's own table — the total, the split, and the percent explicit. */
export function sourceFlowRoleRows(model) {
  return (model?.roles ?? []).map((r) => ({
    role: r.label,
    hours: r.hours,
    explicit_hours: r.explicit_hours,
    explicit_share: r.explicit_share,
    inferred_hours: r.inferred_hours,
    inferred_share: r.inferred_share,
    explicit_of_committed: r.explicit_of_committed,
    recurring_hours: r.recurring_hours,
    nonproject_hours: r.nonproject_hours,
    pipeline_hours: r.pipeline_hours,
    high_confidence_hours: r.high_confidence_hours,
    people: r.people,
    projects: r.projects,
    opportunities: r.opportunities,
    weeks: r.weeks,
  }));
}

/** One row per flow: the bands themselves, which is what the chart is made of. */
export function sourceFlowLinkRows(model) {
  return (model?.flows ?? []).map((f) => ({
    source: f.source_label,
    layer: f.layer,
    role: f.role_label,
    hours: f.hours,
    share_of_role: share(f.hours, (model?.roles ?? []).find((r) => r.key === f.role)?.hours ?? 0),
    share_of_source: share(
      f.hours,
      (model?.sources ?? []).find((s) => s.key === f.source)?.hours ?? 0,
    ),
    confidence_tier: f.confidence_tier ?? null,
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37): specifications, decorated for the screen and read by the CSV writer.

/** One row per role. */
export function sourceFlowRoleColumns() {
  return [
    col.text('role', 'Role'),
    col.hours('hours', 'Hours'),
    col.hours('explicit_hours', 'Explicit'),
    col.percent('explicit_share', 'Explicit share'),
    col.hours('inferred_hours', 'Inferred'),
    col.percent('inferred_share', 'Inferred share'),
    col.percent('explicit_of_committed', 'Explicit of committed'),
    col.hours('recurring_hours', 'Recurring'),
    col.hours('nonproject_hours', 'Non-project'),
    col.hours('pipeline_hours', 'Pipeline'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.count('people', 'People'),
    col.count('projects', 'Projects'),
    col.count('opportunities', 'Deals'),
    col.count('weeks', 'Weeks'),
  ];
}

/** One row per flow. */
export function sourceFlowLinkColumns() {
  return [
    col.text('source', 'Source'),
    col.text('layer', 'Layer'),
    col.text('role', 'Role'),
    col.hours('hours', 'Hours'),
    col.percent('share_of_role', 'Share of the role'),
    col.percent('share_of_source', 'Share of the source'),
    col.text('confidence_tier', 'Confidence'),
  ];
}

/** Every mode the allocation table allows, for the tests that hold the vocabulary complete. */
export const ALL_SOURCE_MODES = SOURCE_MODES;

/** The layer a mode belongs to, re-exported so a caller needs one import, not two. */
export { layerOf };
